import React from 'react'
import ReactApexChart from 'react-apexcharts'
import {ApexOptions} from 'apexcharts'

type Segment = {
  faixa_renda: string
  total_dom: number
}

type Props = {
  segments: Segment[]
  total: number
}

const SegmentedProgressBar: React.FC<Props> = ({segments, total}) => {
  // Calculate the percentage for each segment and the remaining "empty" space
  const sortedSegments = [...segments].sort((a, b) => b.total_dom - a.total_dom)
  const seriesData = sortedSegments.map((segment) => segment.total_dom)
  const totalValue = seriesData.reduce((sum, current) => sum + current, 0)
  const remainingValue = Math.max(0, total - totalValue)

  // Add an "empty" segment to complete the 100% width, if needed
  if (remainingValue > 0) {
    seriesData.push(remainingValue)
  }

  const colors = ['#ff0000', '#FFA500', '#ff0059', '#008000', '#0000FF', '#4B0082', '#EE82EE']
  const options: ApexOptions = {
    chart: {
      type: 'bar',
      height: 100, // Adjust height as needed
      stacked: true, // Key property for segmented progress
      stackType: '100%', // Makes the total width 100%
      toolbar: {
        show: false, // Hide toolbar for a cleaner look
      },
    },
    plotOptions: {
      bar: {
        horizontal: true,
        barHeight: '100%', // Bars take up full height of chart area
        dataLabels: {
          total: {
            enabled: false, // Hide total percentage label
          },
        },
      },
    },
    stroke: {
      width: 0, // Remove stroke between segments
    },
    fill: {
      opacity: 1,
      colors: [...sortedSegments.map((segment, index) => colors[index]), '#e0e0e0'], // Add a default color for the 'empty' space
    },
    legend: {
      show: true, // Show a legend for segments
      position: 'bottom',
      horizontalAlign: 'center',
    },
    xaxis: {
      labels: {
        show: false, // Hide x-axis labels
      },
    },
    yaxis: {
      labels: {
        show: false, // Hide y-axis labels
      },
    },
    tooltip: {
      y: {
        formatter: function (val, {seriesIndex}) {
          // Custom tooltip to show original value and name
          if (seriesIndex < sortedSegments.length) {
            return `${sortedSegments[seriesIndex].faixa_renda}: ${sortedSegments[seriesIndex].total_dom}`
          }
          return '' // Hide tooltip for the 'empty' segment
        },
      },
    },
    grid: {
      show: false, // Hide background grid lines
    },
  }

  const series = [
    {
      name: 'Progress Segments',
      data: [
        {
          x: 'Progress', // Static category name
          y: seriesData,
        },
      ],
    },
  ]

  // Reformat series for a single horizontal bar
  const finalSeries = seriesData.map((value, index) => {
    // Check if it's the empty space segment
    if (index === sortedSegments.length) {
      return {name: 'Remaining', data: [value]}
    }
    return {
      name: sortedSegments[index].faixa_renda,
      data: [value],
    }
  })

  return (
    <ReactApexChart options={options} series={finalSeries} type='bar' width={'100%'} height={100} />
  )
}

export default SegmentedProgressBar
